import React, { useMemo } from "react";
import {
  DevToolsProvider,
  amplitudePlugin,
  envHealthPlugin,
  mobiusLinkPlugin,
  tracesPlugin,
  idmPlugin,
  featureFlagsPlugin,
  toolGroup,
  separator,
  type DevToolsItem,
} from "components/devtools";
import { journeyGroup } from "components/devtools/plugins/groups/journey";

interface Site {
  name: string;
  description: string;
  href: string;
}

interface Variant {
  key: string;
  value: string;
  isControl: boolean;
  isActive: boolean;
}

interface Experiment {
  name: string;
  label: string;
  variants: Variant[];
  hasActiveVariant: boolean;
}

interface DevToolsProps {
  // From DevToolsViewModel
  mainSites: Site[];
  ukSites: Site[];
  usSites: Site[];
  verticals: Record<string, string>;
  domainUserId?: string;
  currentSite: string;
  journeyId?: string;
  verticalForIdmRequest?: string;
  amplitudeExperiments: string[];
  amplitudeExperimentsDetailed?: {
    currentPage: Experiment[];
    allOthers: Experiment[];
  };
  // From AutoFillViewModel
  answerSets?: Record<string, [string, string][]>;
  site?: string;
  fillJourneyAnswersPath?: string;
  fillCustomJourneyAnswersPath?: string;
  isDevelopment?: boolean;
  isLocalDevelopment?: boolean;
  // Seeded from the chopin-devtools-sidebar cookie so SSR paints the right state
  initialSidebarExpanded?: boolean;
}

function DevTools(props: DevToolsProps) {
  const items = useMemo<DevToolsItem[]>(
    () => [
      journeyGroup({
        ...props,
        site: props.site || props.currentSite,
      }),
      ...(props.isDevelopment ? [mobiusLinkPlugin()] : []),
      separator(),
      toolGroup("Experiments", [
        amplitudePlugin({ ...props }),
        idmPlugin({
          ...props,
          verticalForIdmRequest: props.verticalForIdmRequest?.toLowerCase(),
        }),
        featureFlagsPlugin(),
      ]),
      separator(),
      toolGroup("Diagnostics", [
        tracesPlugin(),
        ...(props.isLocalDevelopment ? [envHealthPlugin()] : []),
      ]),
    ],
    // Props come from server-rendered data and don't change within a page lifecycle
    [],
  );

  return (
    <DevToolsProvider
      items={items}
      initialSidebarExpanded={props.initialSidebarExpanded}
    />
  );
}

export default DevTools;
